import type { ReactNode } from 'react';
import { supportedGames } from '@/config/supportedGames';
import { BlogCategory } from '@/constants/blog.const';
import { extractGamePrefixFromPath } from '@/utils/gamePrefix';

export interface NavItem {
  id?: string;
  name: string;
  path: string;
  navItemType: NavItemType;
  icon?: ReactNode;
}

export enum NavItemType {
  RegularPage = 'RegularPage',
  GamePage = 'GamePage',
  BlogPage = 'BlogPage',
}

export interface MenuItemGroup extends NavItem {
  subLinks: NavItem[];
}

export function isMenuItemGroup(
  item: NavItem | MenuItemGroup
): item is MenuItemGroup {
  return 'subLinks' in item;
}

export const isNavItemOrGroupActive = createIsNavItemOrGroupActive({
  games: supportedGames,
  blogCategories: Object.values(BlogCategory),
});

export function createIsNavItemOrGroupActive({
  games,
  blogCategories,
}: {
  games: readonly string[];
  blogCategories: readonly string[];
}) {
  return ({
    item,
    currentPath,
  }: {
    item: NavItem | MenuItemGroup;
    currentPath: string;
  }): boolean => {
    switch (item.navItemType) {
      case NavItemType.RegularPage:
        return isNavItemOrGroupPathActive(item, currentPath, item.path);
      case NavItemType.GamePage: {
        const gamePrefixedPaths = generatePathsWithPrefixes({
          path: item.path,
          prefixes: games,
        });

        return gamePrefixedPaths.some((path) =>
          isNavItemOrGroupPathActive(item, currentPath, path)
        );
      }
      case NavItemType.BlogPage: {
        const blogCategorySuffixedPaths = generatePathsWithSuffixes({
          path: item.path,
          suffixes: blogCategories,
        });

        return blogCategorySuffixedPaths.some((path) =>
          isNavItemOrGroupPathActive(item, currentPath, path)
        );
      }
      default:
        item.navItemType satisfies never;
        throw new Error(`Unknown nav item type: ${item.navItemType}.`);
    }
  };
}

function isNavItemOrGroupPathActive(
  item: NavItem | MenuItemGroup,
  currentPath: string,
  path: string
): boolean {
  return (
    currentPath === path ||
    (isMenuItemGroup(item) && currentPath.startsWith(`${path}/`))
  );
}

export const generatePathWithExistingPrefix =
  createGeneratePathWithExistingPrefix(extractGamePrefixFromPath);

/**
 * Given a current path (like "/dota/news"), a target link ("/news"), and a
 * flag to preserve the prefix, generate the correct href ("/dota/news").
 */
export function createGeneratePathWithExistingPrefix(
  prefixExtractor: (path: string) => string | null
) {
  return ({
    currentPath,
    targetPath,
    matchGamePrefix,
  }: {
    currentPath: string;
    targetPath: string;
    matchGamePrefix: boolean;
  }): string => {
    if (!matchGamePrefix) {
      return targetPath;
    }

    const prefix = prefixExtractor(currentPath);
    if (prefix === null) {
      return targetPath;
    }

    if (targetPath === '/') {
      return `/${prefix}`;
    }
    return `/${prefix}${targetPath}`;
  };
}

export const generatePathsWithPrefixes = ({
  path,
  prefixes,
}: {
  path: string;
  prefixes: readonly string[];
}): string[] => {
  if (path === '/') {
    return [path, ...prefixes.map((prefix) => `/${prefix}`)];
  }

  return [path, ...prefixes.map((prefix) => `/${prefix}${path}`)];
};

export const generatePathsWithSuffixes = ({
  path,
  suffixes,
}: {
  path: string;
  suffixes: readonly string[];
}): string[] => {
  if (path === '/') {
    return [path, ...suffixes.map((suffix) => `/${suffix}`)];
  }

  return [path, ...suffixes.map((suffix) => `${path}/${suffix}`)];
};
